import { useMemo, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { zodResolver } from "@hookform/resolvers/zod"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContentCustom, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { toast } from "@/hooks/useToast"
import { BetterDocConsent } from "@/modules/patients/detail/better-doc/components/better-doc-consent"
import { BetterDocConsentAdditionalInformation } from "@/modules/patients/detail/better-doc/components/better-doc-consent-additional-information"
import { BetterDocConsultationQuestionnaire } from "@/modules/patients/detail/better-doc/de/components/better-doc-consultation-questionnaire/better-doc-consultation-questionnaire"
import { BetterDocPreferredCareProviderSection } from "@/modules/patients/detail/better-doc/de/components/better-doc-preferred-care-provider/better-doc-preferred-care-provider-section"
import { createRequestBetterDocRecommendationInput } from "@/modules/patients/detail/better-doc/de/lib/create-request-better-doc-recommendation-input"
import {
  createDeBetterDocSearchSchema,
  type DeBetterDocSearchFormType,
} from "@/modules/patients/detail/better-doc/de/schema/de-better-doc-search-schema"
import type { BetterDocCareProviderSearchItem } from "@/modules/patients/detail/better-doc/de/services/betterDocCareProviderSearchQuery"
import { fetchDeProgressNoteBetterDocResult } from "@/modules/patients/detail/better-doc/de/services/fetchDeProgressNoteBetterDocResult"
import { useRequestBetterDocRecommendationMutation } from "@/modules/patients/detail/better-doc/de/services/useRequestBetterDocRecommendationMutation"
import type { BetterDocSpecialty } from "@/modules/patients/detail/better-doc/lib/better-doc-sub-specialties"
import type { BetterDocAddress, DeBetterDocInsurance } from "@/modules/patients/detail/better-doc/types"
import { EpisodeOfCareProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import type { PointOfCareType } from "@/types/__generated__/union-types"

type DeBetterDocSearchDialogProps = {
  onClose: () => void
  specialtyName?: BetterDocSpecialty
  pointOfCareSpecialtyId?: string
  isBetterDocSpecialtyFixed?: boolean
  effectivePointOfCare?: PointOfCareType
  episodeOfCareProgressNoteData: EpisodeOfCareProgressNoteData
  address: BetterDocAddress | null
  phoneNumber: string | null
  deBetterDocInsurance: DeBetterDocInsurance | null
}

export const DeBetterDocSearchDialog = ({
  onClose,
  specialtyName,
  pointOfCareSpecialtyId,
  isBetterDocSpecialtyFixed = false,
  effectivePointOfCare,
  episodeOfCareProgressNoteData,
  address,
  phoneNumber,
  deBetterDocInsurance,
}: DeBetterDocSearchDialogProps) => {
  const { t } = useTranslation()
  const [requestBetterDocRecommendation, isRequestingBetterDocRecommendation] =
    useRequestBetterDocRecommendationMutation()
  const [selectedPreferredCareProvider, setSelectedPreferredCareProvider] =
    useState<BetterDocCareProviderSearchItem | null>(null)

  const searchSchema = useMemo(() => createDeBetterDocSearchSchema(specialtyName), [specialtyName])

  const form = useForm<DeBetterDocSearchFormType>({
    resolver: zodResolver(searchSchema),
    defaultValues: {},
  })

  const clearSelectedPreferredCareProvider = () => {
    setSelectedPreferredCareProvider(null)
  }

  const handleFormSubmit = form.handleSubmit((answers) => {
    if (!address) {
      toast({ description: t("episodeOfCare.betterDoc.error.addressNotFound") })
      return
    }

    if (!phoneNumber) {
      toast({ description: t("episodeOfCare.betterDoc.error.phoneNumberNotFound") })
      return
    }

    if (!deBetterDocInsurance) {
      toast({ description: t("episodeOfCare.betterDoc.error.insuranceNotFound") })
      return
    }

    if (!effectivePointOfCare) {
      return
    }

    const input = createRequestBetterDocRecommendationInput({
      progressNoteId: episodeOfCareProgressNoteData.id,
      effectivePointOfCare,
      pointOfCareSpecialtyId: isBetterDocSpecialtyFixed ? null : (pointOfCareSpecialtyId ?? null),
      answers,
      specialtyName,
      preferredMedicActivityId: selectedPreferredCareProvider?.medhubActivityId ?? null,
      address,
      phoneNumber,
      insurance: deBetterDocInsurance,
    })

    requestBetterDocRecommendation({
      variables: { input },
      onCompleted: () => {
        void fetchDeProgressNoteBetterDocResult(episodeOfCareProgressNoteData.id).catch(() => null)
        FaroInsightsWrapper.pushEvent("better_doc_consent_send")
        onClose()
      },
      onError: () => {
        void fetchDeProgressNoteBetterDocResult(episodeOfCareProgressNoteData.id).catch(() => null)
      },
    })
  })

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContentCustom
        onPointerDownOutside={(e) => e.preventDefault()}
        data-testid="better-doc-dialog-content"
        className="flex h-[calc(100vh-120px)] flex-col">
        <DialogHeader>
          <DialogTitle data-testid="better-doc-dialog-title">
            {t("episodeOfCare.betterDoc.searchDialog.title")}
          </DialogTitle>
        </DialogHeader>
        <FormProvider {...form}>
          <div className="mx-6 my-4 flex justify-between">
            <div className="flex max-w-142 grow flex-col items-start gap-8" data-testid="consent">
              <BetterDocConsent />
              {specialtyName && (
                <BetterDocConsultationQuestionnaire
                  specialtyName={specialtyName}
                  isBetterDocSpecialtyFixed={isBetterDocSpecialtyFixed}
                />
              )}
              {effectivePointOfCare && (
                <BetterDocPreferredCareProviderSection
                  progressNoteId={episodeOfCareProgressNoteData.id}
                  effectivePointOfCare={effectivePointOfCare}
                  specialtyId={isBetterDocSpecialtyFixed ? null : (pointOfCareSpecialtyId ?? null)}
                  selectedCareProvider={selectedPreferredCareProvider}
                  onSelect={setSelectedPreferredCareProvider}
                  onRemove={clearSelectedPreferredCareProvider}
                />
              )}
            </div>
            <BetterDocConsentAdditionalInformation />
          </div>
          <DialogFooter className="mt-auto flex p-6 pt-4" data-testid="better-doc-dialog-footer">
            <Button className="px-4 py-2" variant="secondary" onClick={onClose} data-testid="cancel-button">
              {t("common.button.cancel")}
            </Button>
            <Button
              isLoading={isRequestingBetterDocRecommendation}
              className="px-4 py-2"
              variant="primary"
              data-testid="send-request-button"
              onClick={handleFormSubmit}>
              {t("common.button.sendRequest")}
            </Button>
          </DialogFooter>
        </FormProvider>
      </DialogContentCustom>
    </Dialog>
  )
}
